import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Divider,
  Tooltip,
  TextField,
  InputAdornment,
  Button,
  //Avatar,
  IconButton as MuiIconButton,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  ViewModule as ViewModuleIcon,
  Construction as ConstructionIcon,
  Diversity3 as Diversity3Icon,
  Explore as ExploreIcon,
  AccountBalance as AccountBalanceIcon,
  Mic as MicIcon,
  //Edit as EditIcon,
  Send as SendIcon,
  SmartToy as SmartToyIcon,
  People as PeopleIcon,
  Forum as ForumIcon,
  SupportAgent as SupportAgentIcon,
  Search as SearchIcon,
  Public as PublicIcon,
  AssignmentTurnedIn as AssignmentTurnedInIcon,
  EmojiEvents as EmojiEventsIcon,
  Add as AddIcon,
  //CheckCircle as CheckIcon,
  Healing as DiagnosticIcon,
  AccountTree as DecisionIcon,
  Insights as PredictiveIcon,
  Create as CreativeIcon,
  //Info as InfoIcon
} from '@mui/icons-material';
//import { StepIconProps } from '@mui/material/StepIcon';
import HolographicStepper, { StepData } from '../common/HolographicStepper.tsx';
import HolographicCard from '../common/HolographicCard.tsx';
import ProblemSourcingDrawer from '../workspace/ProblemSourcingDrawer.tsx';
import GradientText from '../common/GradientText.tsx';
//import ProblemSolvingToolbox from '../../pages/Home/components/ProblemSolvingToolbox.tsx';

// Define interfaces
interface NavigationStepperProps {
  problemText: string;
  setProblemText: (text: string) => void;
  handleExecutiveSelect?: (executive: any) => void; 
  toggleExecutivesDrawer?: () => void;
  executives?: any[];
  handleMainFunctionSelect?: (functionId: string) => void;
  handleViewProposal?: (id: string) => void;
  proposals?: any[];
  problemTitle?: string;
  handleStepperComplete?: () => void;
  onStepChange?: (step: number) => void;
  initialStep?: number;
}

interface StepContentProps {
  problemText: string;
  setProblemText: (text: string) => void;
  handleExecutiveSelect?: (executive: any) => void;
  toggleExecutivesDrawer?: () => void;
  executives?: any[];
  handleMainFunctionSelect?: (functionId: string) => void;
  handleViewProposal?: (id: string) => void;
  proposals?: any[];
  problemTitle?: string;
  handleStepperComplete?: () => void;
  handleEditProblemText?: () => void;
  handleSaveProblemText?: () => void;
  handleCancelEditProblemText?: () => void;
  handleShowProblemSourcingDrawer?: () => void;
  isEditingProblemText?: boolean;
  showProblemSourcingDrawer?: boolean;
  setShowProblemSourcingDrawer?: (show: boolean) => void;
}

// Styled components
const ContentBox = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  background: 'rgba(26, 26, 46, 0.4)',
  borderRadius: theme.spacing(1),
  border: '1px solid rgba(138, 43, 226, 0.2)',
  marginBottom: theme.spacing(2),
}));

// Add these styled components for better scrolling
const StepperContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  overflow: 'hidden',
}));

const ScrollableContent = styled(Box)(({ theme }) => ({
  flexGrow: 1,
  overflowY: 'auto',
  overflowX: 'hidden',
  padding: theme.spacing(2),
  '&::-webkit-scrollbar': {
    width: '8px',
  },
  '&::-webkit-scrollbar-track': {
    background: 'rgba(26, 26, 46, 0.4)',
    borderRadius: '4px',
  },
  '&::-webkit-scrollbar-thumb': {
    background: 'rgba(138, 43, 226, 0.3)',
    borderRadius: '4px',
    '&:hover': {
      background: 'rgba(138, 43, 226, 0.5)',
    },
  },
}));

// Main NavigationStepper component
const NavigationStepper: React.FC<NavigationStepperProps> = ({
  problemText,
  setProblemText,
  handleExecutiveSelect,
  toggleExecutivesDrawer,
  executives = [],
  handleMainFunctionSelect,
  handleViewProposal,
  proposals = [],
  problemTitle,
  handleStepperComplete,
  onStepChange,
  initialStep = 0
}) => {
  // State variables
  const [activeStep, setActiveStep] = useState<number>(initialStep);
  const [isEditingProblemText, setIsEditingProblemText] = useState<boolean>(false);
  // eslint-disable-next-line no-unused-vars
  const [_editedProblemText, _setEditedProblemText] = useState<string>(problemText);
  const [showProblemSourcingDrawer, setShowProblemSourcingDrawer] = useState<boolean>(false);
  
  // Keep these handlers but make sure they're used somewhere
  const handleEditProblemText = () => {
    setIsEditingProblemText(true);
    _setEditedProblemText(problemText);
  };

  const handleSaveProblemText = () => {
    setProblemText(_editedProblemText);
    setIsEditingProblemText(false);
  };

  const handleCancelEditProblemText = () => {
    setIsEditingProblemText(false);
  };

  const handleShowProblemSourcingDrawer = () => {
    setShowProblemSourcingDrawer(true);
  };

  // Create props object to pass to step components
  const stepProps: StepContentProps = {
    problemText,
    setProblemText,
    handleExecutiveSelect,
    toggleExecutivesDrawer,
    executives,
    handleMainFunctionSelect,
    handleViewProposal,
    proposals,
    problemTitle,
    handleStepperComplete,
    handleEditProblemText,
    handleSaveProblemText,
    handleCancelEditProblemText,
    handleShowProblemSourcingDrawer,
    isEditingProblemText,
    showProblemSourcingDrawer,
    setShowProblemSourcingDrawer
  };
  
  // Define navigation steps with proper props
  const navigationSteps: StepData[] = [
    {
      label: 'Create',
      description: 'Define, enhance, and structure your challenge',
      icon: <ViewModuleIcon />,
      content: <CreateStep {...stepProps} />
    },
    {
      label: 'Forge',
      description: 'Refine AI prompts and problem-solving strategies',
      icon: <ConstructionIcon />,
      content: <ForgeStep {...stepProps} />
    },
    {
      label: 'Engage',
      description: 'Collaborate with AI, experts, and communities',
      icon: <Diversity3Icon />,
      content: <EngageStep {...stepProps} />
    },
    {
      label: 'Explore',
      description: 'Discover insights, solutions, and recommendations',
      icon: <ExploreIcon />,
      content: <ExploreStep {...stepProps} />
    },
    {
      label: 'Valuation',
      description: 'Assess scope, success criteria, benefits, and impact',
      icon: <AccountBalanceIcon />,
      content: <ValuationStep {...stepProps} />
    }
  ];

  const handleStepChange = (newStep: number) => {
    setActiveStep(newStep);
    if (onStepChange) {
      onStepChange(newStep);
    }
  };

  console.log('Received setProblemText:', typeof setProblemText);

  return (
    <StepperContainer>
      <HolographicStepper
        steps={navigationSteps}
        initialStep={activeStep}
        onChange={handleStepChange}
        orientation="horizontal"
        alternativeLabel={true}
        showContent={false} // Important: don't let the stepper control content display
      />
      
      <ScrollableContent>
        {navigationSteps[activeStep].content}
      </ScrollableContent>
    </StepperContainer>
  );
};

// Step Content Components
const CreateStep: React.FC<StepContentProps> = ({ 
  problemText, 
  setProblemText,
  handleExecutiveSelect,
  toggleExecutivesDrawer,
  executives,
  handleMainFunctionSelect,
  handleViewProposal,
  proposals,
  problemTitle,
  handleStepperComplete,
  handleEditProblemText,
  handleSaveProblemText,
  handleCancelEditProblemText,
  handleShowProblemSourcingDrawer,
  isEditingProblemText,
  showProblemSourcingDrawer,
  setShowProblemSourcingDrawer
}) => {
  // Helper function to safely set the drawer visibility
  const safeSetShowProblemSourcingDrawer = (value: boolean) => {
    if (setShowProblemSourcingDrawer) {
      setShowProblemSourcingDrawer(value);
    } else {
      console.warn('setShowProblemSourcingDrawer is not defined');
    }
  };

  return (
    <Box>
      {/* Problem Input Section */}
      <Box sx={{ mb: 4 }}>
        <Paper
          elevation={0}
          sx={{
            p: 2,
            backgroundColor: 'rgba(26, 26, 46, 0.7)',
            borderRadius: 1,
            border: '1px solid rgba(138, 43, 226, 0.3)',
            display: 'flex',
            alignItems: 'center'
          }}
        >
          <Box>
            <TextField
              fullWidth
              multiline
              rows={3}
              placeholder="Start working on a new Problem"
              variant="outlined"
              value={problemText || ''}
              onChange={(e) => setProblemText(e.target.value)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title="Add to Proposal">
                      <MuiIconButton 
                        edge="end" 
                        onClick={() => {
                          if (problemText) {
                            // Call LLM API with problemText
                            console.log('Sending to LLM:', problemText);
                          }
                        }}
                        disabled={!problemText}
                        sx={{
                          color: '#00CC99',
                          '&.Mui-disabled': {
                            color: 'rgba(255, 255, 255, 0.3)',
                          }
                        }}
                      >
                        <SendIcon />
                      </MuiIconButton>
                    </Tooltip>
                  </InputAdornment>
                ),
                startAdornment: (
                  <InputAdornment position="start">
                    <Tooltip title="Open problem submission drawer">
                      <MuiIconButton 
                        edge="start" 
                        onClick={() => safeSetShowProblemSourcingDrawer(true)}
                        sx={{ color: 'rgba(138, 43, 226, 0.7)' }}
                      >
                        <MicIcon />
                      </MuiIconButton>
                    </Tooltip>
                  </InputAdornment>
                ),
                sx: {
                  backgroundColor: 'rgba(26, 26, 46, 0.4)',
                  color: '#fff',
                  '&.Mui-focused': {
                    boxShadow: '0 0 0 2px rgba(138, 43, 226, 0.5)',
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(138, 43, 226, 0.3)',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(138, 43, 226, 0.5)',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(138, 43, 226, 0.7)',
                  },
                }
              }}
              sx={{
                '& .MuiInputBase-input': {
                  color: '#fff',
                },
              }}
            />
            
            {/* Add button to open ProblemSourcingDrawer */}
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => safeSetShowProblemSourcingDrawer(!showProblemSourcingDrawer)}
              sx={{
                mt: 1,
                borderColor: 'rgba(138, 43, 226, 0.5)',
                color: 'rgba(255, 255, 255, 0.9)',
                '&:hover': {
                  borderColor: '#8A2BE2',
                  backgroundColor: 'rgba(138, 43, 226, 0.1)',
                }
              }}
            >
              <GradientText gradient="primary" variant="body2">
                {showProblemSourcingDrawer ? 'Close Problem Sourcing' : 'Use Advanced Problem Sourcing'}
              </GradientText>
            </Button>
            
            {/* Conditionally render the ProblemSourcingDrawer */}
            {showProblemSourcingDrawer && (
              <ProblemSourcingDrawer 
                onSubmit={(problem) => {
                  console.log('Attempting to call setProblemText with:', problem);
                  console.log('setProblemText is:', typeof setProblemText);
                  if (typeof setProblemText === 'function') {
                    setProblemText(problem);
                  } else {
                    console.error('setProblemText is not a function:', setProblemText);
                  }
                  safeSetShowProblemSourcingDrawer(false);
                }}
                onClose={() => safeSetShowProblemSourcingDrawer(false)}
              />
            )}
          </Box>
        </Paper>
        <Typography variant="caption" sx={{ mt: 1, display: 'block', color: 'rgba(255, 255, 255, 0.7)' }}>
          Describe your problem or paste existing text to get started
        </Typography>
      </Box>
    </Box>
  );
};

const ForgeStep: React.FC<StepContentProps> = ({ 
  problemText, 
  executives = [],
  handleExecutiveSelect
}) => {
  // Function to handle toolbox opening
  const handleOpenToolbox = () => {
    console.log('Opening Problem Solving Toolbox');
    // Here you would typically implement the logic to show the toolbox
    // e.g., through state management or navigation
  };

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 3, color: '#fff' }}>
        Knowledge Toolbox
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <HolographicCard sx={{ height: '100%', p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, color: '#fff' }}>
              Diagnostic Methods
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 2 }}>
              SWOT Analysis, Five Whys, Root Cause Analysis
            </Typography>
            <Button
              variant="contained"
              startIcon={<DiagnosticIcon />}
              sx={{
                background: 'linear-gradient(45deg, #8A2BE2 30%, #9370DB 90%)',
              }}
              onClick={handleOpenToolbox}
            >
              Add to Proposal
            </Button>
          </HolographicCard>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <HolographicCard sx={{ height: '100%', p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, color: '#fff' }}>
              Decision-Making Methods
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 2 }}>
              Decision Matrix, PDCA Cycle, Scenario Planning
            </Typography>
            <Button
              variant="contained"
              startIcon={<DecisionIcon />}
              sx={{
                background: 'linear-gradient(45deg, #00CC99 30%, #8A2BE2 90%)',
              }}
              onClick={() => console.log('Decision-Making Methods clicked')}
            >
              Add to Proposal
            </Button>
          </HolographicCard>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <HolographicCard sx={{ height: '100%', p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, color: '#fff' }}>
              Predictive & Analytical
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 2 }}>
              Data Visualization, Predictive Modeling, A/B Testing
            </Typography>
            <Button
              variant="contained"
              startIcon={<PredictiveIcon />}
              sx={{
                background: 'linear-gradient(45deg, #FF6F00 30%, #8A2BE2 90%)',
              }}
            >
              Add to Proposal
            </Button>
          </HolographicCard>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <HolographicCard sx={{ height: '100%', p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, color: '#fff' }}>
              Creative Problem-Solving
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 2 }}>
              Design Thinking, Lateral Thinking, Brainstorming, First-Principles Thinking
            </Typography>
            <Button
              variant="contained"
              startIcon={<CreativeIcon />}
              sx={{
                background: 'linear-gradient(45deg, #003366 30%, #8A2BE2 90%)',
              }}
            >
              Add to Proposal
            </Button>
          </HolographicCard>
        </Grid>

        <Grid item xs={12} md={6}>
          <HolographicCard sx={{ height: '100%', p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, color: '#fff' }}>
              Strategic Innovation
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 2 }}>
              Systems Thinking, Blue Ocean Strategy, Disruptive Innovation, Business Value Canvas
            </Typography>
            <Button
              variant="contained"
              startIcon={<SearchIcon />}
              sx={{
                background: 'linear-gradient(45deg, #003366 30%, #8A2BE2 90%)',
              }}
            >
              Add to Proposal
            </Button>
          </HolographicCard>
        </Grid>
      </Grid>
    </Box>
  );
};

const EngageStep: React.FC<StepContentProps> = ({ 
  toggleExecutivesDrawer,
  handleMainFunctionSelect
}) => {
  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 3, color: '#fff' }}>
        Collaboration Hub
      </Typography>
      
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <HolographicCard sx={{ height: '100%', p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, color: '#fff' }}>
              AI Surrogates
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 2 }}>
              Work with automated agents with expertise in key disciplines.
            </Typography>
            <Button
              variant="contained"
              startIcon={<SmartToyIcon />}
              sx={{
                background: 'linear-gradient(45deg, #8A2BE2 30%, #9370DB 90%)',
              }}
              onClick={() => handleMainFunctionSelect && handleMainFunctionSelect('executives')}
            >
              Engage AI Surrogates
            </Button>
          </HolographicCard>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <HolographicCard sx={{ height: '100%', p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, color: '#fff' }}>
              Human Collaboration
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 2 }}>
              Connect with specialists, consultants, and team members.
            </Typography>
            <Button
              variant="contained"
              startIcon={<PeopleIcon />}
              sx={{
                background: 'linear-gradient(45deg, #00CC99 30%, #8A2BE2 90%)',
              }}
            >
              Invite Collaborators
            </Button>
          </HolographicCard>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <HolographicCard sx={{ height: '100%', p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, color: '#fff' }}>
              Community Discussions
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 2 }}>
              Participate in forums, group brainstorming, and shared insights.
            </Typography>
            <Button
              variant="contained"
              startIcon={<ForumIcon />}
              sx={{
                background: 'linear-gradient(45deg, #FF6F00 30%, #8A2BE2 90%)',
              }}
            >
              Browse Forums
            </Button>
          </HolographicCard>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <HolographicCard sx={{ height: '100%', p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, color: '#fff' }}>
              Customer Support
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 2 }}>
              Access chatbots, live support, FAQs, and documentation.
            </Typography>
            <Button
              variant="contained"
              startIcon={<SupportAgentIcon />}
              sx={{
                background: 'linear-gradient(45deg, #003366 30%, #8A2BE2 90%)',
              }}
            >
              Get Support
            </Button>
          </HolographicCard>
        </Grid>
      </Grid>
    </Box>
  );
};

const ExploreStep: React.FC<StepContentProps> = ({ 
  proposals = [], 
  handleViewProposal,
  handleMainFunctionSelect
}) => {
  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 3, color: '#fff' }}>
        Explore Insights & Solutions
      </Typography>
      
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <HolographicCard sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, color: '#fff' }}>
              Active Proposals & Solutions
            </Typography>
            
            <Grid container spacing={2}>
              {proposals.map((proposal) => (
                <Grid item xs={12} md={6} key={proposal.id}>
                  <Paper
                    sx={{
                      p: 2,
                      backgroundColor: 'rgba(26, 26, 46, 0.8)',
                      borderRadius: 1,
                      border: '1px solid rgba(0, 204, 153, 0.3)',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-3px)',
                        boxShadow: '0 5px 15px rgba(0, 204, 153, 0.2)',
                      }
                    }}
                    onClick={() => handleViewProposal && handleViewProposal(proposal.id)}
                  >
                    <Typography variant="subtitle1" sx={{ color: '#00CC99' }}>
                      {proposal.title}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 1 }}>
                      {proposal.description}
                    </Typography>
                    {proposal.status && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box
                          component="span"
                          sx={{
                            px: 1,
                            py: 0.5,
                            fontSize: '0.75rem',
                            backgroundColor: 'rgba(0, 204, 153, 0.1)',
                            color: '#00CC99',
                            border: '1px solid rgba(0, 204, 153, 0.3)',
                            borderRadius: '4px',
                          }}
                        >
                          {proposal.status}
                        </Box>
                        <Typography variant="body2" sx={{ color: '#00CC99' }}>
                          ${proposal.amount?.toLocaleString() || 0}
                        </Typography>
                      </Box>
                    )}
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </HolographicCard>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <HolographicCard sx={{ height: '100%', p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, color: '#fff' }}>
              Similar Problems
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 2 }}>
              Discover analogies and related challenges.
            </Typography>
            <Button
              variant="contained"
              startIcon={<SearchIcon />}
              sx={{
                background: 'linear-gradient(45deg, #8A2BE2 30%, #00CC99 90%)',
              }}
              onClick={() => handleMainFunctionSelect && handleMainFunctionSelect('similar')}
            >
              Browse Similar Problems
            </Button>
          </HolographicCard>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <HolographicCard sx={{ height: '100%', p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, color: '#fff' }}>
              Internet Research
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 2 }}>
              Access curated sources, search functionalities, and idea aggregation.
            </Typography>
            <Button
              variant="contained"
              startIcon={<PublicIcon />}
              sx={{
                background: 'linear-gradient(45deg, #003366 30%, #00CC99 90%)',
              }}
            >
              Research Web
            </Button>
          </HolographicCard>
        </Grid>
      </Grid>
    </Box>
  );
};

const ValuationStep: React.FC<StepContentProps> = ({ 
  problemTitle, 
  handleStepperComplete 
}) => {
  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 3, color: '#fff' }}>
        Valuation & Impact Assessment
      </Typography>
      
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <HolographicCard sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, color: '#fff' }}>
              Solution Valuation
            </Typography>
            
            {/* Placeholder for FinancialPlanStepper */}
            <ContentBox sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ mb: 1, color: '#fff' }}>
                Financial Plan for {problemTitle || 'Your Solution'}
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                Develop budget projections, investment strategies, and financial metrics.
              </Typography>
              <Divider sx={{ my: 2, bgcolor: 'rgba(138, 43, 226, 0.2)' }} />
              <Button
                variant="contained"
                sx={{
                  background: 'linear-gradient(45deg, #8A2BE2 30%, #9370DB 90%)',
                }}
                onClick={handleStepperComplete}
              >
                Complete Valuation
              </Button>
            </ContentBox>
          </HolographicCard>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <HolographicCard sx={{ height: '100%', p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, color: '#fff' }}>
              Success Criteria
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 2 }}>
              Define what success looks like for your solution.
            </Typography>
            <Button
              variant="contained"
              startIcon={<AssignmentTurnedInIcon />}
              sx={{
                background: 'linear-gradient(45deg, #00CC99 30%, #8A2BE2 90%)',
              }}
            >
              Define Success Criteria
            </Button>
          </HolographicCard>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <HolographicCard sx={{ height: '100%', p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, color: '#fff' }}>
              Contribution Attribution
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 2 }}>
              Manage attribution and recognition settings.
            </Typography>
            <Button
              variant="contained"
              startIcon={<EmojiEventsIcon />}
              sx={{
                background: 'linear-gradient(45deg, #FF6F00 30%, #8A2BE2 90%)',
              }}
            >
              View Contributor Dashboard
            </Button>
          </HolographicCard>
        </Grid>
      </Grid>
    </Box>
  );
};

export default NavigationStepper;